import React, { useState, useEffect } from 'react';
import AnimateHeight from 'react-animate-height';
import { Box } from '@kaizen/component-library';
import { Button } from '@kaizen/button';
import { TextAreaField, LikertScaleLegacy } from '@kaizen/components';
import { RatingScale as RatingScaleType, ScaleItem } from 'ca-capture/types';
import notifySavingError from 'ca-capture/helpers/notifySavingError';
import styles from './PostSurveyForm.scss';
import request from 'ca-request';
import { Heading } from '@kaizen/typography';
import type { ColorSchema } from '@kaizen/components/src/LikertScaleLegacy/types';
import {
  ampli,
  PostSurveyFeedbackSubmitted,
} from '../../../../../../app/ampli';
import { sendAmpliEvent } from '../../../../../../app/ampli/sendAmpliEvent';
import { AmpliProvider } from '@cultureamp/analytics';

type Props = {
  scale: RatingScaleType;
  scaleColorSchema: ColorSchema;
  responsePath: string;
  redirectPath: string;
  uiText: {
    ratingLabel: string;
    textAreaLabel: string;
    textAreaDescription: string;
    submitLabel: string;
    submittingLabel: string;
    submitError: string;
  };
  surveyAggregateId: string;
};

const PostSurveyForm = (props: Props): React$Element<string> => {
  const { scale, scaleColorSchema, responsePath, redirectPath, uiText } = props;
  const [partTwoHeight, setPartTwoHeight] = useState<0 | 'auto'>(0);
  const [ratingSelection, setRatingSelection] = useState<ScaleItem | null>(
    scale.find((i) => i.value === -1) || null
  );
  const [suggestionValue, setSuggestion] = useState<string>('');
  const [formStatus, setFormStatus] = useState<
    'default' | 'submitting' | 'error'
  >('default');

  useEffect(() => {
    if (!ratingSelection || ratingSelection.value < 0) {
      setPartTwoHeight(0);
    } else {
      setPartTwoHeight('auto');
    }
  }, [ratingSelection]);

  const onRatingSelect = (value: ScaleItem | null) => {
    setRatingSelection(value);
  };

  const onSuggestionChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSuggestion(event.target.value);
  };

  const onFormSubmit = (): void => {
    if (formStatus === 'submitting') {
      // Prevent multiple requests while form is submitting
      return;
    }

    setFormStatus('submitting');

    const data = {
      field: 'murmur_feedback',
      answer: ratingSelection ? ratingSelection.value : -1,
      murmur_suggestion: suggestionValue,
    };
    request
      .put(responsePath, {
        body: JSON.stringify(data),
      })
      .then(() => {
        const event = new PostSurveyFeedbackSubmitted({
          Rating: ratingSelection?.value,
        });
        sendAmpliEvent({
          ...event,
          group_properties: { Survey: props.surveyAggregateId },
        });

        window.location.replace(redirectPath);
      })
      .catch((error) => {
        setFormStatus('error');
        notifySavingError(
          error,
          responsePath,
          'post survey form',
          uiText.submitError
        );
      });
  };

  return (
    <AmpliProvider ampli={ampli}>
      <PostSurveyRating
        scale={scale}
        scaleColorSchema={scaleColorSchema}
        selectedItem={ratingSelection}
        onSelect={onRatingSelect}
        uiText={uiText}
      />
      <AnimateHeight height={partTwoHeight}>
        <Box mt={0.5}>
          <PostSurveyTextArea
            suggestionValue={suggestionValue}
            onSuggestionChange={onSuggestionChange}
            uiText={uiText}
          />
          <Button
            label={
              formStatus === 'submitting'
                ? uiText.submittingLabel
                : uiText.submitLabel
            }
            reversed
            onClick={onFormSubmit}
          />
        </Box>
      </AnimateHeight>
    </AmpliProvider>
  );
};

type RatingProps = {
  scale: RatingScaleType;
  scaleColorSchema: ColorSchema;
  onSelect: (value: ScaleItem | null) => void;
  selectedItem: ScaleItem | null;
  uiText: {
    ratingLabel: string;
  };
};

const PostSurveyRating = (props: RatingProps) => {
  const { scale, scaleColorSchema, onSelect, selectedItem, uiText } = props;
  const likertScaleId = 'post-survey-likert';

  return (
    <>
      <Heading tag="div" variant="heading-5" color="white" id={likertScaleId}>
        {uiText.ratingLabel}
      </Heading>
      <div className={styles.ratingWrapper}>
        <LikertScaleLegacy
          labelId={likertScaleId}
          scale={scale}
          colorSchema={scaleColorSchema}
          onSelect={onSelect}
          selectedItem={selectedItem}
          reversed
        />
      </div>
    </>
  );
};

type TextAreaProps = {
  suggestionValue: string;
  onSuggestionChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  uiText: {
    textAreaLabel: string;
    textAreaDescription: string;
  };
};

const PostSurveyTextArea = (props: TextAreaProps) => {
  const { suggestionValue, onSuggestionChange, uiText } = props;

  return (
    <div className={styles.textAreaWrapper}>
      <TextAreaField
        id="post-survey-textarea"
        labelText={uiText.textAreaLabel}
        rows={6}
        reversed
        onChange={onSuggestionChange}
        value={suggestionValue}
        description={uiText.textAreaDescription}
      />
    </div>
  );
};

export default PostSurveyForm;
